import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import Header from '../Header/Header';
import history from "../Routes/history";
import ConfirmMail from './ConfirmMail';
import MailParams from './MailParams';
import GenPdf from '../GenPdf';
import InformationDialog from '../InformationDialog';
import { changeIndication } from "../../redux/actions/emailAction";
import LoadingOverlay from '../LoadingOverlay';
import "../../Styles/Mail/createMail.scss"

const DEFAULT_CC = [
    "coordogec@coordogec.fr",
    "jeannette.wonje@coordogec.fr",
    "jacob.ilunga@coordogec.fr",
    "slimane.lami@coordogec.fr"
];

const CreateMail = ({ match, user, attachment, indication, fileName, typeFile, title, moInfos, dossierValue, dossierInfos, lotValue, vicValue }) => {
    const dispatch = useDispatch();
    const isMountedRef = useRef(true);

    const [mailData, setMailData] = useState({
        sender: "",
        password: "",
        to: "",
        cc: "",
        cci: "",
        subject: "",
        text: "",
        filename: "",
    });

    const [uiState, setUiState] = useState({
        loading: false,
        disableButton: false,
        openDialog: false,
        openInfoDialog: false,
        pdf: false,
        file: null,
        infoMessage: "",
        countdown: null,
        showCountdown: false,
    });

    // Cleanup effect pour éviter les fuites mémoire
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            dispatch(changeIndication(""));
        };
    }, [dispatch]);

    // Fonction pour update state seulement si le composant est monté
    const safeSetUiState = useCallback((updater) => {
        if (isMountedRef.current) {
            setUiState(updater);
        }
    }, []);

    const safeSetMailData = useCallback((updater) => {
        if (isMountedRef.current) {
            setMailData(updater);
        }
    }, []);

    // Fonction de navigation
    const getRedirectPath = useCallback(() => {
        return title === "VIC" ? "/vic/page-1" :
            title === "Registre Journal" ? "/rj/evaluation" :
                title === "Contrat" ? "/contrat/données" :
                    title === "Avenant Echéances" ? "/contrat/echeances" :
                        title === "PGC" ? "/pgc/page1" :
                            title === "Pré DIUO" ? "/prediuo/page1" :
                                title === "DIUO" ? "/diuo/page1" :
                                    title === "Proposition de Devis" ? "/devis/proposition" :
                                        title === "Calcul Approximatif" ? "/devis/calcul" :
                                            title.includes("Facture Conception") ? "/facture/conception" :
                                                title.includes("Facture Réalisation") ? "/facture/realisation" :
                                                    title.includes("Facture Récapitulative") ? "/facture/recapitulative" :
                                                        title.includes("1ème Relance") ? "/facture/relance" :
                                                            title.includes("2ème Relance") ? "/facture/relance" :
                                                                title.includes("réglement intérieur") ? "/cissct/reglement-interieur" :
                                                                    title.includes("Convocation CISSCT") ? "/cissct/convocation" :
                                                                        title.includes("Procès-verbal") ? "/cissct/proces-verbal" :
                                                                            "/mailing/suivi-mail"
    }, [title]);

    // Fonction pour démarrer le décompte
    const startCountdown = useCallback(() => {
        safeSetUiState(prev => ({
            ...prev,
            countdown: 5,
            showCountdown: true
        }));
    }, [safeSetUiState]);

    // Ajoutez cet useEffect pour gérer le countdown
    useEffect(() => {
        let countdownInterval = null;

        if (uiState.showCountdown && uiState.countdown !== null) {
            if (uiState.countdown > 0) {
                countdownInterval = setTimeout(() => {
                    safeSetUiState(prev => {
                        if (!isMountedRef.current) {
                            return prev;
                        }

                        if (prev.countdown <= 1) {
                            const redirectPath = getRedirectPath();
                            if (redirectPath) {
                                history.push(redirectPath);
                            }
                            return {
                                ...prev,
                                countdown: 0,
                                showCountdown: false
                            };
                        }

                        return {
                            ...prev,
                            countdown: prev.countdown - 1
                        };
                    });
                }, 1000);
            }
        }

        return () => {
            if (countdownInterval) {
                clearTimeout(countdownInterval);
            }
        };
    }, [uiState.showCountdown, uiState.countdown, safeSetUiState, getRedirectPath]);

    // Pour la prop information du dialog, utilisez cette version :
    const getDialogInformation = () => {
        if (uiState.showCountdown && uiState.countdown !== null) {
            return (
                <>
                    {uiState.infoMessage}
                    <br />
                    Redirection dans {uiState.countdown} seconde{uiState.countdown > 1 ? 's' : ''}...
                </>
            );
        }
        return uiState.infoMessage;
    };

    // Fonction pour générer le contenu du texte selon le type de document
    const getContentText = useCallback((title) => {
        if (title === "VIC") {
            return `Bonjour,
Dans le cadre du chantier mentionné en objet, veuillez trouver en pièce jointe votre VIC établie suivant les informations habituelles vous concernant et celles de votre entreprise.
Si vous estimez devoir préciser des observations sur le contenu de cette VIC après en avoir pris connaissance, nous sommes à votre disposition pour une mise à jour.
Dans l'attente de votre retour éventuel.
Bien cordialement.`;
        }

        const getText = () => {
            const factureRealisationMatch = title.match(/^Facture Réalisation R(\d+)$/);
            if (factureRealisationMatch) {
                return `e la facture réalisation R${factureRealisationMatch[1]}`;
            }

            const textMap = {
                "Registre Journal": ` le Registre Journal`,
                "Calcul Approximatif": ` le devis approximatif`,
                "Contrat": ` votre contrat`,
                "Avenant Echéances": ` l'avenant d'echéances`,
                "PGC": ` le PGC`,
                "Pré DIUO": ` le pré-DIUO`,
                "DIUO": ` le DIUO`,
                "Facture Conception C1": `e la facture conception C1`,
                "Facture Conception C2": `e la facture conception C2`,
                "1ère Relance": `e la 1ère Relance`,
                "2ème Relance": `e la 2ème Relance`,
                "Règlement intérieur": ` le règlement intérieur du CISSCT`,
                "Convocation CISSCT": `e la convocation du CISSCT`,
                "Procès-verbal": ` le procès-verbal du CISSCT`
            };

            return textMap[title] || ``;
        };

        return `Bonjour,
Veuillez trouver ci-joint${getText()}
Bien cordialement,`;
    }, []);

    // Fonction pour récupérer l'email du responsable de l'entreprise (VIC uniquement)
    const getContractorMail = useCallback(async () => {
        let contractorMail = "";

        if (window.location.href.includes('vic')) {
            if (vicValue) {
                try {
                    const response = await fetch(`/api/mail/getRespCompanyMail/${vicValue}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                        },
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        console.log('Erreur lors de la récupération de l\'email du responsable:', errorData);
                    }

                    const data = await response.json();

                    if (data.success && data.respCompanyMail) {
                        contractorMail = data.respCompanyMail;
                    }

                } catch (error) {
                    console.error('Erreur réseau lors de la récupération de l\'email du responsable:', error);
                }
            }
        }

        return contractorMail;
    }, [vicValue]);

    // Fonction pour récupérer toutes les adresses mails déjà utilisées pour le dossier
    const getMailsDossier = useCallback(async () => {
        try {
            const response = await fetch(`/api/mail/getMailsByDossier/${dossierValue}`);
            if (!response.ok) return [];
            return await response.json();
        } catch (error) {
            console.error('Erreur lors de la récupération des mails du dossier:', error);
            return [];
        }
    }, [dossierValue]);

    // Initialisation des données au montage du composant
    useEffect(() => {
        const initializeData = async () => {
            const vicUrl = window.location.href.includes('vic');
            const rjUrl = window.location.href.includes('rj');

            if (user.length > 0 && user[0].email) {
                const userEmail = user[0].email;
                safeSetMailData(prev => ({
                    ...prev,
                    sender: userEmail,
                    filename: fileName,
                    cc: DEFAULT_CC.join(", ")
                }));
            }

            if (moInfos) {
                safeSetUiState(prev => ({ ...prev, loading: true }));
                const [toEmail, mailsDossier] = await Promise.all([getContractorMail(), getMailsDossier()]);
                safeSetUiState(prev => ({ ...prev, loading: false }));

                // Ordre du Cc : MO en premier, puis les mails du dossier, puis les Cc par défaut à la fin
                // (les adresses déjà dans "À" et les Cc par défaut sont exclues du début)
                const ccList = [];
                const seen = new Set([...DEFAULT_CC, toEmail.trim().toLowerCase()]);
                [moInfos.mail, ...mailsDossier].forEach(email => {
                    const clean = (email || "").trim().toLowerCase();
                    if (clean && !seen.has(clean)) {
                        seen.add(clean);
                        ccList.push(clean);
                    }
                });
                ccList.push(...DEFAULT_CC);

                const subjectText = `DOSSIER ${dossierValue.toString().split("").join("-")} : ${dossierInfos.operation} - ${vicUrl ? fileName : rjUrl ? (title + " N°" + fileName.split("_")[2]) : title}`;

                safeSetMailData(prev => ({
                    ...prev,
                    to: toEmail,
                    cc: ccList.join(", "),
                    subject: subjectText,
                    text: getContentText(title)
                }));
            }
        };

        initializeData();
    }, [user, fileName, moInfos, dossierValue, dossierInfos, title, getContractorMail, getMailsDossier, getContentText, safeSetMailData, safeSetUiState]);

    // Mise à jour lors des changements de props
    useEffect(() => {
        if (user.length > 0) {
            safeSetMailData(prev => ({ ...prev, sender: user[0].email }));
        }
    }, [user, safeSetMailData]);

    useEffect(() => {
        const url = window.location.href.includes('vic');
        const subjectText = `DOSSIER ${dossierValue.toString().split("").join("-")} : ${dossierInfos.operation} - ${url ? fileName : title}`;
        safeSetMailData(prev => ({ ...prev, subject: subjectText }));
    }, [title, dossierValue, dossierInfos, fileName, safeSetMailData]);

    useEffect(() => {
        safeSetMailData(prev => ({ ...prev, filename: fileName }));
    }, [fileName, safeSetMailData]);

    // Extraction des emails d'une chaîne de caractères
    const extractEmails = (inputString) => {
        const emailRegex = /[\w.-]+@[\w-]+(?:\.[\w-]{2,})+/g;
        const emails = inputString.match(emailRegex);
        return emails ? emails.join(", ") : "";
    };

    // Gestion des changements dans les champs de formulaire
    const handleChange = (event) => {
        const { name, value } = event.target;
        setMailData(prev => ({ ...prev, [name]: value }));
    };

    // Envoi du mail
    const handleSubmit = async () => {
        safeSetUiState(prev => ({ ...prev, disableButton: true }));

        let blob;
        try {
            if (!attachment) {
                throw new Error('Aucune pièce jointe disponible');
            }
            const attachmentResponse = await fetch(attachment);
            if (!attachmentResponse.ok) {
                throw new Error(`Impossible de récupérer la pièce jointe (statut ${attachmentResponse.status})`);
            }
            blob = await attachmentResponse.blob();
        } catch (attachError) {
            console.error('Erreur lors de la récupération du fichier à joindre:', attachError);
            safeSetUiState(prev => ({
                ...prev,
                infoMessage: "Impossible de récupérer le fichier PDF à joindre. Veuillez régénérer le document et réessayer.",
                openInfoDialog: true,
                disableButton: false
            }));
            return;
        }

        const formData = new FormData();
        const { type } = match.params;

        const toFormatted = extractEmails(mailData.to);
        const ccFormatted = extractEmails(mailData.cc);
        const cciFormatted = extractEmails(mailData.cci);

        const emailContent = `
        <div style="font-family: Aptos, Arial, sans-serif; font-size: 14px; line-height: 1.6;">
          ${mailData.text.replace(/\n/g, '<br>')}
        </div>
      `;

        formData.append("ndossier", dossierValue);
        formData.append("indication", indication);
        formData.append("typefile", typeFile);
        formData.append("title", title);
        formData.append("sender", mailData.sender);
        formData.append("password", mailData.password);
        formData.append("to", toFormatted.split(",").map(email => email.trim()));
        formData.append("cc", ccFormatted.split(",").map(email => email.trim()));
        formData.append("cci", cciFormatted.split(",").map(email => email.trim()));
        formData.append("subject", mailData.subject);
        formData.append("text", emailContent);
        formData.append("filename", mailData.filename);
        formData.append("type_notif", type);
        formData.append("attachment", blob);
        
        const controller = new AbortController();
        const TIMEOUT_MS = 30000;
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        try {
            const response = await fetch('/api/mail/sendMailPdf', {
                method: 'POST',
                body: formData,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                throw new Error(`Le serveur a renvoyé une erreur (statut ${response.status})${errorText ? ` : ${errorText}` : ''}`);
            }

            const result = await response.text();

            safeSetMailData(prev => ({
                ...prev,
                to: toFormatted,
                cc: ccFormatted,
                cci: cciFormatted,
                text: result === 'OK' ? '' : prev.text
            }));

            const isSuccess = result === 'OK';
            const message = isSuccess
                ? 'Votre mail a bien été envoyé avec succès'
                : 'Une erreur est survenue lors de l\'envoi du mail';

            safeSetUiState(prev => ({
                ...prev,
                infoMessage: message,
                openInfoDialog: true,
                disableButton: false
            }));

            if (isSuccess) {
                startCountdown();
            }

        } catch (error) {
            clearTimeout(timeoutId);

            let infoMessage;

            if (error.name === 'AbortError') {
                console.error('Timeout lors de l\'envoi du mail (délai de 25s dépassé):', error);
                infoMessage = "L'envoi a pris trop de temps et a été interrompu. Réessayez dans quelques instants ; si le problème persiste, contactez le support technique.";
            } else if (error.message && error.message.includes('NetworkError')) {
                console.error('Erreur réseau lors de l\'envoi:', error);
                infoMessage = "Une erreur réseau est survenue. Vérifiez votre connexion et réessayez ; si le problème persiste, contactez le support technique.";
            } else {
                console.error('Erreur lors de l\'envoi:', error);
                infoMessage = 'Une erreur est survenue lors de l\'envoi du mail';
            }

            safeSetUiState(prev => ({
                ...prev,
                infoMessage,
                openInfoDialog: true,
                disableButton: false
            }));
        }
    };

    // Affichage du PDF
    const showPDF = async () => {
        try {
            const blob = await fetch(attachment).then(res => res.blob());
            if (blob && isMountedRef.current) {
                safeSetUiState(prev => ({ ...prev, file: blob, pdf: true }));
            }
        } catch (error) {
            console.error('Erreur lors du chargement du PDF:', error);
        }
    };

    // Gestionnaires d'événements pour les dialogues
    const handleOpenDialog = () => safeSetUiState(prev => ({ ...prev, openDialog: true }));
    const handleCloseDialog = () => safeSetUiState(prev => ({ ...prev, openDialog: false }));
    const closeDoc = () => safeSetUiState(prev => ({ ...prev, pdf: false }));
    const handleCloseInfoDialog = () => {
        safeSetUiState(prev => ({ ...prev, openInfoDialog: false }));

        if (uiState.showCountdown) {
            // Rediriger immédiatement vers la page appropriée
            const redirectPath = getRedirectPath();
            if (redirectPath) {
                history.push(redirectPath);
            }
            // Nettoyer le countdown
            safeSetUiState(prev => ({ ...prev, showCountdown: false, countdown: null }));
        }
    };

    return (
        <div className='create-mail'>
            <Header />
            {uiState.loading && <LoadingOverlay />}

            <div className="create-mail__container">
                <div className="create-mail__header">
                    <h1 className='create-mail__title'>Envoi de {title}</h1>
                </div>

                <div className="create-mail__content">
                    <MailParams
                        {...mailData}
                        disableButton={uiState.disableButton}
                        user={user}
                        title={title}
                        attachment={attachment}
                        typeFile={typeFile}
                        handleChange={handleChange}
                        handleOpenDialog={handleOpenDialog}
                        showPDF={showPDF}
                    />
                </div>
            </div>

            <GenPdf
                file={uiState.file}
                isOpen={uiState.pdf}
                closeDoc={closeDoc}
            />

            <ConfirmMail
                open={uiState.openDialog}
                onConfirm={handleSubmit}
                setOpen={handleCloseDialog}
                getTo={mailData.to}
                getSubject={mailData.subject}
                getText={mailData.text}
                getSender={mailData.sender}
            />

            <InformationDialog
                isOpen={uiState.openInfoDialog}
                title="INFORMATION"
                information={getDialogInformation()}
                handleClose={handleCloseInfoDialog}
            />
        </div>
    );
};

const mapStateToProps = state => ({
    user: state.user.items,
    attachment: state.email.file,
    indication: state.email.indication,
    fileName: state.email.filename,
    typeFile: state.email.typefile,
    title: state.email.title,
    moInfos: state.contact.moInfos,
    dossierValue: state.devis.dossierValue,
    dossierInfos: state.dossier.item,
    lotValue: state.vic.lotValue,
    vicValue: state.vic.vicValue,
});

export default connect(mapStateToProps)(CreateMail);